import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useRTL } from "../../../../domain/language/use-rtl";
import { ChevronIcon } from "../../../common/svg/chevron";
import { theme } from "../../../styles/theme";

interface OptionCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle: string;
  onClick: () => void;
  children: JSX.Element;
}

export function OptionCard(props: OptionCardProps) {
  const { title, subtitle, onClick, children } = props;
  const { isRTL } = useRTL();

  return (
    <Card type="button" $isRTL={isRTL} onClick={onClick}>
      <IconContainer>{children}</IconContainer>
      <MainContainer>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </MainContainer>
      <ChevronContainer>
        <ChevronIcon
          width="14"
          height="14"
          fill={theme.colors.blue[800]}
          style={{ transform: isRTL ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </ChevronContainer>
    </Card>
  );
}

/* One row of the list, not a card of its own: the list around them carries the chrome. */
const Card = styled.button<{ $isRTL: boolean }>`
  width: 100%;
  border: 0;
  font: inherit;
  text-align: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.gray[100]};
  background-color: #ffffff;

  &:first-of-type {
    border-top: 0;
  }

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.gray[50]};
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  object-fit: contain;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 16px;
`;

const Title = styled.span`
  ${theme.text}
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const Subtitle = styled.span`
  ${theme.text}
  margin-top: 2px;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.gray[800]};
`;

const ChevronContainer = styled.div`
  display: flex;
  flex-shrink: 0;
`;
